import React, { useState } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";

/** Two-field emergency-revoke confirmation.  NO typed phrase. */
export function EmergencyRevokeDialog({ open, onClose, onConfirm, entity }) {
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const canSubmit = reason.trim().length >= 10 && ack && !submitting;

  if (!open) return null;

  const handleConfirm = async () => {
    setErr(null); setSubmitting(true);
    try { await onConfirm(reason.trim()); onClose(); }
    catch (e) { setErr(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div
      data-testid="emergency-revoke-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-lg w-full rounded-lg bg-white p-6 border-2 border-red-200 shadow-xl">
        <div className="flex items-start gap-3 mb-4">
          <ShieldAlert className="text-red-600 mt-1 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-red-900">Emergency revoke — remove from patients now</h2>
            <p className="text-sm text-slate-600 mt-1">
              This immediately removes <span className="font-medium">{entity?.canonical_name || "this entity"}</span> from
              patient visibility and invalidates its grounding authorization. It is recorded permanently in the audit log.
            </p>
          </div>
        </div>

        <label className="block text-sm font-medium text-slate-800 mb-1" htmlFor="er-reason">
          Reason (required, minimum 10 characters)
        </label>
        <textarea
          id="er-reason"
          data-testid="ecosystem-emergency-revoke-reason"
          className="w-full border rounded p-2 text-sm mb-1"
          rows={3}
          placeholder="e.g. incorrect information published; partner relationship terminated; safety concern reported"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="text-xs text-slate-500 mb-3">{reason.trim().length}/10 characters</div>

        <label className="flex items-start gap-2 text-sm mb-4 select-none cursor-pointer">
          <input
            data-testid="ecosystem-emergency-revoke-consequence"
            type="checkbox"
            className="mt-1"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <span>
            I understand this immediately removes the entity from patient visibility, invalidates any grounding
            authorization, and is recorded permanently.
          </span>
        </label>

        {err && (
          <div data-testid="emergency-revoke-error" className="mb-3 text-sm text-red-700">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            data-testid="ecosystem-emergency-revoke-cancel"
            className="px-3 py-1.5 text-sm rounded border"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            data-testid="ecosystem-emergency-revoke-confirm"
            className="px-3 py-1.5 text-sm rounded bg-red-700 text-white disabled:bg-red-300"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            Confirm emergency revoke
          </button>
        </div>
      </div>
    </div>
  );
}

/** Acknowledgement dialog for enhanced approvals — one deliberate click. */
export function EnhancedApprovalDialog({ open, onClose, onConfirm, triggerLabel }) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;

  const handle = async () => {
    setErr(null); setSubmitting(true);
    try { await onConfirm(); onClose(); }
    catch (e) { setErr(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div
      data-testid="enhanced-approval-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-md w-full rounded-lg bg-white p-6 border-2 border-amber-300 shadow-xl">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="text-amber-600 mt-1 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-amber-900">Enhanced approval</h2>
            <p className="text-sm text-slate-700 mt-1">
              You are approving <span className="font-medium">{triggerLabel || "an enhanced-review entity"}</span>.
              Enhanced-review approvals carry heightened compliance responsibility.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Classification source: server-provided safety_classification.enhanced_review = true.
            </p>
          </div>
        </div>
        {err && (
          <div data-testid="enhanced-approval-error" className="text-sm text-red-700 mb-2">{err}</div>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <button
            data-testid="ecosystem-enhanced-cancel"
            className="px-3 py-1.5 text-sm rounded border"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            data-testid="ecosystem-enhanced-acknowledge"
            className="px-3 py-1.5 text-sm rounded bg-amber-600 text-white disabled:bg-amber-300"
            onClick={handle}
            disabled={submitting}
          >
            Approve under enhanced tier
          </button>
        </div>
      </div>
    </div>
  );
}

/** Small reason dialog used for reject/deactivate. */
export function ReasonDialog({ open, title, description, onClose, onConfirm, minLength = 1, submitLabel = "Confirm" }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  if (!open) return null;
  const ok = reason.trim().length >= minLength && !submitting;
  const handle = async () => {
    setErr(null); setSubmitting(true);
    try { await onConfirm(reason.trim()); onClose(); setReason(""); }
    catch (e) { setErr(e); }
    finally { setSubmitting(false); }
  };
  return (
    <div
      data-testid="reason-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-md w-full rounded-lg bg-white p-5 border shadow-xl">
        <h3 className="font-semibold mb-1">{title}</h3>
        {description && <p className="text-sm text-slate-600 mb-3">{description}</p>}
        <textarea
          data-testid="reason-input"
          className="w-full border rounded p-2 text-sm"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {err && <div data-testid="reason-error" className="text-sm text-red-700 mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-3">
          <button data-testid="reason-cancel" className="px-3 py-1.5 text-sm rounded border" onClick={onClose} disabled={submitting}>Cancel</button>
          <button data-testid="reason-confirm" className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white disabled:bg-slate-400" disabled={!ok} onClick={handle}>{submitLabel}</button>
        </div>
      </div>
    </div>
  );
}
